import { DateTime } from "luxon";

// use this function if face timezone issue
export function formatDate(dateString: any, format = "yyyy-MM-dd") {
  return dateString && DateTime.fromISO(dateString, { zone: "utc" }).toFormat(format);
}

export function formatDateFromJSDate(dateString: any, format = "yyyy-MM-dd") {
  return dateString && DateTime.fromJSDate(dateString).toFormat(format);
}

/**
 * Parse a date-only API value (e.g. "2026-08-01") into a Date anchored at LOCAL
 * midnight of that same calendar day.
 *
 * `new Date("2026-08-01")` parses the string as UTC midnight, so reading it back
 * with local getters (or Luxon's `{ zone: "local" }`) rolls it back a day for any
 * client behind UTC (all US timezones) - the classic date-of-birth "off by one" bug.
 * Reading the y/m/d from the UTC instant and rebuilding a local Date keeps the
 * calendar day stable regardless of the viewer's timezone.
 */
export function parseDateOnlyToLocalDate(dateString?: string | null): Date | null {
  if (!dateString) return null;
  const dt = DateTime.fromISO(dateString, { zone: "utc" });
  if (!dt.isValid) return null;
  return new Date(dt.year, dt.month - 1, dt.day);
}

/**
 * JSON string like `["2025-01-01","2025-12-31"]` for anniversary filter query params
 * (matches dashboard PoliciesTable).
 */
export function formatAnniversaryDateRangeQueryJson(dates: Date[] | null): string | null {
  if (!dates || dates.length === 0) return null;
  const parts = dates
    .map((d) => formatDateFromJSDate(d, "yyyy-MM-dd"))
    .filter((s): s is string => Boolean(s));
  if (parts.length === 0) return null;
  const range = parts.length === 1 ? [parts[0], parts[0]] : parts.slice(0, 2);
  return JSON.stringify(range);
}

/**
 * JSON string like `["11-13","11-15"]` (MM-dd) for plan anniversary key filtering.
 */
export function formatMonthDayRangeQueryJson(dates: Date[] | null): string | null {
  if (!dates || dates.length === 0) return null;
  const parts = dates
    .map((d) => formatDateFromJSDate(d, "MM-dd"))
    .filter((s): s is string => Boolean(s));
  if (parts.length === 0) return null;
  const range = parts.length === 1 ? [parts[0], parts[0]] : parts.slice(0, 2);
  return JSON.stringify(range);
}

export function getEndDate(date: any) {
  const originalTime = DateTime.fromISO(date, { zone: "Asia/Kolkata" });
  return originalTime.endOf("day");
}

export function getDiffs(dt: string, diffIn: string[] = ["day"]) {
  const date = DateTime.fromJSDate(new Date(dt));
  return date.diffNow(diffIn).toObject();
}
export function isVerifiedDateWithinWeek(dt: any): boolean {
  if (!dt) return false;
  const diff = getDiffs(dt, ["day"]);
  const days = Math.abs(diff.days);
  if (days <= 7.0) {
    return true;
  }
  return false;
}

export function formatDateToLocalTime(date: string, format: Intl.DateTimeFormatOptions) {
  const selectedDateLocal = new Date(date);
  const selectedDateInLocalTimezone = selectedDateLocal.toLocaleString(undefined, format);
  return selectedDateInLocalTimezone;
}

export function formatDateToSubmit(date: string | Date, format: string) {
  let selectedDateObj: Date;

  if (date instanceof Date) {
    // If it's already a Date object, use it directly
    selectedDateObj = date;
  } else {
    // If it's a string, convert to Date
    selectedDateObj = new Date(date);
  }

  // Use local timezone to prevent date shifts during formatting
  const formattedDate = DateTime.fromJSDate(selectedDateObj, { zone: "local" }).toFormat(format);
  return formattedDate;
}

/**
 * Format date for API submission without timezone conversion issues
 * Creates a date string in local timezone to prevent date shifts
 */
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format date for API submission handling both Date objects and strings
 * Ensures consistent yyyy-MM-dd format without timezone conversion issues
 */
export function formatDateForSubmission(date: Date | string): string {
  if (date instanceof Date) {
    // Format Date object to yyyy-MM-dd without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  // If it's a string, use formatDateToSubmit
  return formatDateToSubmit(date, "yyyy-MM-dd");
}

/**
 * Format date as ISO DateTime for API submission without timezone conversion issues
 * Creates an ISO DateTime string at start of day without timezone conversion
 */
export function formatDateAsISODateTime(date: Date | string): string {
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  // Extract date components in local timezone
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const day = dateObj.getDate();

  // Create date at start of day in local timezone
  const startOfDay = new Date(year, month, day, 0, 0, 0, 0);

  // Format as ISO string manually to avoid timezone conversion
  const isoYear = startOfDay.getFullYear();
  const isoMonth = String(startOfDay.getMonth() + 1).padStart(2, "0");
  const isoDay = String(startOfDay.getDate()).padStart(2, "0");
  const isoHours = String(startOfDay.getHours()).padStart(2, "0");
  const isoMinutes = String(startOfDay.getMinutes()).padStart(2, "0");
  const isoSeconds = String(startOfDay.getSeconds()).padStart(2, "0");
  const isoMilliseconds = String(startOfDay.getMilliseconds()).padStart(3, "0");

  return `${isoYear}-${isoMonth}-${isoDay}T${isoHours}:${isoMinutes}:${isoSeconds}.${isoMilliseconds}Z`;
}

/**
 * Format date as ISO DateTime for API submission without timezone conversion issues
 * Creates an ISO DateTime string at end of day (23:59:59.999) without timezone conversion
 */
export function formatDateAsEndOfDayISODateTime(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T23:59:59.999Z`;
}

// Convert like August 3, 2023
export function formatDateToWordDate(date: string, month = "long" as any) {
  const selectedDateObj = new Date(date);
  return selectedDateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month,
    day: "numeric",
  });
}

export function formatDateToWordDateWithoutLocal(dateObj: any) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = dateObj.getUTCDate();
  const month = monthNames[dateObj.getUTCMonth()];
  const year = dateObj.getUTCFullYear();

  return `${month} ${day}, ${year}`;
}

export function formatDateWordDateWithoutLocal(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function customFormatDate(
  dateTimeString: string,
  format: string = "MM/dd/yyyy hh:mm a",
  timezone: string = "America/Chicago",
  dateType?: string,
): string {
  if (dateTimeString) {
    const dateTime = DateTime.fromISO(dateTimeString, { zone: timezone });
    if (dateType) {
      return `${dateTime.toFormat(format)} ${dateType} `;
    }
    return dateTime.toFormat(format);
  }
  return "";
}

/**
 * Relative-day timestamp for "last activity" style fields, anchored to CST:
 * - "Today 10:10 AM CST" / "Yesterday 4:16 PM CST" when the CST calendar day matches
 * - "MM/dd/yyyy" (the CST calendar day) otherwise
 *
 * Both the Today/Yesterday bucketing and the printed time run through America/Chicago,
 * so the value doesn't drift with the viewer's browser zone (IAP-10591).
 */
export function formatRelativeDayTimeCST(dateString?: string | null): string {
  if (!dateString) return "";
  const date = DateTime.fromISO(dateString, { zone: "America/Chicago" });
  if (!date.isValid) return "";

  const today = DateTime.now().setZone("America/Chicago");
  const time = `${date.toFormat("h:mm a")} CST`;
  if (date.toISODate() === today.toISODate()) return `Today ${time}`;
  if (date.toISODate() === today.minus({ days: 1 }).toISODate()) return `Yesterday ${time}`;
  return date.toFormat("MM/dd/yyyy");
}

export function formatAsUSDWithTwoDecimals(number: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

/**
 * Formats a notification date as:
 * - 'Today at h:mm A' if today
 * - 'Yesterday at h:mm A' if yesterday
 * - 'MM/DD/YYYY' otherwise
 * @param dateString ISO date string
 * @returns formatted string
 */
export function formatNotificationDate(dateString: string): string {
  if (!dateString) return "";
  const date = DateTime.fromISO(dateString);
  const cstTime = date.setZone("America/Chicago");

  const now = DateTime.now();

  if (date.hasSame(now, "day")) {
    return `Today at ${cstTime.toFormat("hh")}:${cstTime.toFormat("mm")} ${cstTime
      .toFormat("a")
      ?.toUpperCase()} CST`;
  }
  if (date.hasSame(now.minus({ days: 1 }), "day")) {
    return `Yesterday at ${cstTime.toFormat("hh")}:${cstTime.toFormat("mm")} ${cstTime
      .toFormat("a")
      ?.toUpperCase()} CST`;
  }
  return date.toFormat("MM/dd/yyyy");
}

export function getCurrentAndNextQuarter(isoDateString: string) {
  // Parse in UTC to avoid timezone shifts
  const date = DateTime.fromISO(isoDateString, { zone: "utc" });

  const currentQuarter = date.quarter;

  // Calculate next quarter and wrap around if needed
  const nextQuarter = currentQuarter === 4 ? 1 : currentQuarter + 1;

  return {
    currentQuarter: `Q${currentQuarter}`,
    nextQuarter: `Q${nextQuarter}`,
  };
}

/**
 * "just now" under a minute, otherwise Luxon's relative time, e.g. "2 minutes ago" /
 * "3 hours ago". For "Last saved …" style labels.
 */
export const formatTimeAgo = (date: Date, now: Date = new Date()): string => {
  const base = DateTime.fromJSDate(now);
  const then = DateTime.fromJSDate(date);
  if (base.diff(then, "seconds").seconds < 60) return "just now";
  return then.toRelative({ base }) ?? "";
};
